// `@sl/core/queries` — TanStack Query option factories for the 0.1 endpoints (ARCH §8.1, §9.3; T-023).
//
// How a screen uses them:
//   const queries = createQueries(client, { freshness: freshnessOf(account) });   // once, in the app's provider
//   const summary = useQuery(queries.summary(accountId, periodQueryOf(view, account.today)));
//   queryClient.getQueryData(queries.summary(accountId, q).queryKey)                // typed through DataTag
//   await invalidateAfter(queryClient, 'requestSync', accountId)                   // invalidation.ts
//
// Design rules (framework-agnostic — no React; only `@tanstack/query-core`):
// - **The `ApiClient` is injected** — `createQueries(client, options)` closes over it. Every factory then takes
//   plain arguments, so `useQuery(queries.summary(id, q))` reads like the contract and a test hands in a fake
//   client. A per-call `client` argument was the alternative: noisier at every call site, and the client is
//   one per app anyway (it comes from the app's ApiClient context, ARCH §8.2).
// - **Runtime imports from `@tanstack/query-core` are exactly one: `keepPreviousData`.** `queryOptions()` is
//   not in query-core at 5.103.2 (it lives in `@tanstack/react-query` as a typed identity), so `define()`
//   below is that identity: it tags the key with the data and error types (`DataTag`), which is what makes
//   `getQueryData(key)` / `setQueryData(key, …)` typed in the apps. Everything else is a type import.
// - **Keys** come from `keys.ts`; **stale time** from `staleTime.ts` (60 s demo / `refreshMinutes` live);
//   every period-keyed read carries `placeholderData: keepPreviousData` so a period switch keeps the last
//   numbers on screen until the new ones land (REQ-280 AC2; the idle prefetch is `prefetch.ts`). **The kept
//   answer is still an answer about ITS period** (T-356, D-876 — BUG-200): a sentence beside those figures takes
//   its period words from the answer's own `meta.period` (`answeredPeriod`, `@sl/core/periods`), never from the
//   control that has already moved on; a label that cannot be the answer's goes pending with its figure
//   (REQ-170 AC4). A factory whose consumers refuse the kept answer outright carries no `keepPreviousData`
//   (`alertBacktest`, D-873).
// - **Network mode** (T-356, D-879): a demo client's factories carry `networkMode: 'always'` — the server is
//   in-process and never waits for the network, so a failed read's retry is never left `paused` by a browser
//   that reports itself offline, and every declared error state is reachable by address (`define.ts`).
// - Fetchers return the whole `ApiResponse<T>` — screens read `meta` (warnings, `stale`, `historyLoadedBackTo`,
//   `period`), never just `data`.
// - Endpoints the demo server delivers later (`entities*`, `tree`, `adsManagerLink` → T-025) reject with
//   `NotImplementedError` today; the factory shape is what T-037 / T-039 / T-041 build against.

import { keepPreviousData } from '@tanstack/query-core';
import type { ApiClient, PeriodQuery } from '../api/ApiClient';
import type {
  AccountSummary,
  EntityDetail,
  EntityIndexItem,
  EntityList,
  EntityTree,
  TimeSeries,
} from '../types/entities';
import type { ActivityEntry, ChangeSet, ImpactPreview } from '../types/changes';
import type { AdPreview, CreativeDetail, CreativeTile, Placement } from '../types/creatives';
import type { ApiResponse, IsoMonth } from '../types/primitives';
import type { AccountInfo, AdsManagerLink, Session, SyncStatus } from '../types/session';
import type { AccountSettings, ResultOption, UserPreferences } from '../types/settings';
import type { Allocation, Pacing, SpendPlan, WastedSpend } from '../types/spend';
import {
  queryKeys,
  type ActivityQuery,
  type AllocationQuery,
  type CreativesQuery,
  type EntitiesQuery,
  type PacingQuery,
  type TimeseriesQuery,
} from './keys';
import { staleTimeFor, type Freshness } from './staleTime';
import { createAlertQueries } from './alerts';
import { createHomeQueries } from './home';
import { define, queryBase, requirePeriod } from './define';

export type { SlQueryOptions } from './define';

export interface CreateQueriesOptions {
  /** The account's mode and refresh interval — `freshnessOf(account)`. Absent before the account has loaded:
   *  the client's transport decides (demo → 60 s, http → the live default). */
  freshness?: Freshness | null;
}

/** Polling cadence of `syncStatus` while `state === 'running'`. */
export const SYNC_POLL_MS = 2_000;

export type Queries = ReturnType<typeof createQueries>;

/** All factories for one `ApiClient` — see the header comment. */
export function createQueries(client: ApiClient, options: CreateQueriesOptions = {}) {
  const staleTime = staleTimeFor(options.freshness, client.transport);
  // What every factory below shares: the stale time, and on a demo client the `always` network mode (D-879).
  const base = queryBase(client.transport, staleTime);

  return {
    /** The stale time every factory below uses (60 s demo / `refreshMinutes` live). */
    staleTime,

    // -- user-scoped -------------------------------------------------------------------------------------------
    session: () =>
      define<ApiResponse<Session>, ReturnType<typeof queryKeys.session>>({
        queryKey: queryKeys.session(),
        queryFn: () => client.getSession(),
        ...base,
      }),
    preferences: () =>
      define<ApiResponse<UserPreferences>, ReturnType<typeof queryKeys.preferences>>({
        queryKey: queryKeys.preferences(),
        queryFn: () => client.getPreferences(),
        ...base,
      }),

    // -- account shell -----------------------------------------------------------------------------------------
    account: (accountId: string) =>
      define<ApiResponse<AccountInfo>, ReturnType<typeof queryKeys.accountInfo>>({
        queryKey: queryKeys.accountInfo(accountId),
        queryFn: () => client.getAccount(accountId),
        ...base,
      }),
    syncStatus: (accountId: string) =>
      define<ApiResponse<SyncStatus>, ReturnType<typeof queryKeys.sync>>({
        queryKey: queryKeys.sync(accountId),
        queryFn: () => client.getSync(accountId),
        ...base,
        refetchInterval: (query) =>
          query.state.data?.data.state === 'running' ? SYNC_POLL_MS : false,
      }),

    // Every period-keyed read chains `requirePeriod` (T-364, D-892's amendment): an answer with no `meta.period` is
    // malformed, not slow — the read rejects with `malformed_answer` and the surface takes its declared error state.
    // -- Home (API §3.1–3.2) — period-keyed ---------------------------------------------------------------------
    summary: (accountId: string, q: PeriodQuery) =>
      define<ApiResponse<AccountSummary>, ReturnType<typeof queryKeys.summary>>({
        queryKey: queryKeys.summary(accountId, q),
        queryFn: () => client.getSummary(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    timeseries: (accountId: string, q: TimeseriesQuery) =>
      define<ApiResponse<TimeSeries>, ReturnType<typeof queryKeys.timeseries>>({
        queryKey: queryKeys.timeseries(accountId, q),
        queryFn: () => client.getTimeSeries(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),

    // -- Hierarchy (API §4) -------------------------------------------------------------------------------------
    entitiesIndex: (accountId: string) =>
      define<ApiResponse<EntityIndexItem[]>, ReturnType<typeof queryKeys.entitiesIndex>>({
        queryKey: queryKeys.entitiesIndex(accountId),
        queryFn: () => client.getEntityIndex(accountId),
        ...base,
      }),
    entities: (accountId: string, q: EntitiesQuery) =>
      define<ApiResponse<EntityList>, ReturnType<typeof queryKeys.entitiesList>>({
        queryKey: queryKeys.entitiesList(accountId, q),
        queryFn: () => client.getEntities(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    entity: (accountId: string, entityId: string, q: PeriodQuery) =>
      define<ApiResponse<EntityDetail>, ReturnType<typeof queryKeys.entity>>({
        queryKey: queryKeys.entity(accountId, entityId, q),
        queryFn: () => client.getEntity(accountId, entityId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    tree: (accountId: string, q: PeriodQuery) =>
      define<ApiResponse<EntityTree>, ReturnType<typeof queryKeys.tree>>({
        queryKey: queryKeys.tree(accountId, q),
        queryFn: () => client.getEntityTree(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    adsManagerLink: (accountId: string, entityId: string) =>
      define<ApiResponse<AdsManagerLink>, ReturnType<typeof queryKeys.adsManagerLink>>({
        queryKey: queryKeys.adsManagerLink(accountId, entityId),
        queryFn: () => client.getAdsManagerLink(accountId, entityId),
        ...base,
      }),

    // -- Spend & budget (API §5) — the plan and pacing key on a MONTH, the map and the wasted panel on a period
    spendPlan: (accountId: string, month?: IsoMonth) =>
      define<ApiResponse<SpendPlan>, ReturnType<typeof queryKeys.spendPlan>>({
        queryKey: queryKeys.spendPlan(accountId, month),
        queryFn: () => client.getSpendPlan(accountId, month),
        ...base,
      }),
    spendPacing: (accountId: string, q: PacingQuery = {}) =>
      define<ApiResponse<Pacing>, ReturnType<typeof queryKeys.spendPacing>>({
        queryKey: queryKeys.spendPacing(accountId, q),
        queryFn: () => client.getPacing(accountId, q),
        ...base,
        // REQ-071 AC5's This month / Last month is a key change like a period switch: keep the last numbers up.
        placeholderData: keepPreviousData,
      }),
    spendAllocation: (accountId: string, q: AllocationQuery) =>
      define<ApiResponse<Allocation>, ReturnType<typeof queryKeys.spendAllocation>>({
        queryKey: queryKeys.spendAllocation(accountId, q),
        queryFn: () => client.getAllocation(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    spendWasted: (accountId: string, q: PeriodQuery) =>
      define<ApiResponse<WastedSpend>, ReturnType<typeof queryKeys.spendWasted>>({
        queryKey: queryKeys.spendWasted(accountId, q),
        queryFn: () => client.getWasted(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),

    // -- Change-sets (API §6.1, §6.5) — the open tray and its review (T-125) --------------------------------
    // Neither is period-keyed: the tray is the tray whatever dates the screen is showing. `changeSet` is the
    // query ARCH §9.3 calls "staged changes are server state": every screen reads it, and the rows show staged
    // values by merging its items on with `withStagedMarkers` from `@sl/core/rows` — **lists are not refetched
    // when something is staged** (REQ-101 AC1, T-129's Accept). `changePreview` is a POST that stores nothing,
    // so it is a query like any other.
    changeSet: (accountId: string) =>
      define<ApiResponse<ChangeSet>, ReturnType<typeof queryKeys.changeSet>>({
        queryKey: queryKeys.changeSet(accountId),
        queryFn: () => client.getChangeSet(accountId),
        ...base,
      }),
    changePreview: (accountId: string, itemIds?: readonly string[]) =>
      define<ApiResponse<ImpactPreview>, ReturnType<typeof queryKeys.changePreview>>({
        queryKey: queryKeys.changePreview(accountId, itemIds),
        queryFn: () =>
          client.previewChanges(accountId, itemIds === undefined ? undefined : [...itemIds]),
        ...base,
        // Deliberately NO `keepPreviousData` (REQ-104 AC6): deselecting a change makes a new key, and holding
        // the old selection's forecast under the new checkboxes would put two things on screen that contradict
        // each other. A period switch re-answers the same question; this one asks a different one.
      }),

    // -- Activity log (API §7.1) — T-126 -----------------------------------------------------------------
    // The log is the one read in this product whose answer GROWS as the owner uses it, so it is the one that
    // is paginated: `client.getActivity` takes a `cursor` and `meta.page.nextCursor` says whether there is
    // more. The factory takes the FILTERS and leaves the cursor to the caller (`useInfiniteQuery`'s
    // `pageParam`), because a second page is more of the same answer, not a different question — which is why
    // `cursor` is not in the key. `keepPreviousData` on purpose: changing the range filter on the log screen
    // should not blank the list while the next page loads (REQ-106 AC2's own range control).
    activity: (accountId: string, q: ActivityQuery = {}, cursor?: string) =>
      define<ApiResponse<ActivityEntry[]>, ReturnType<typeof queryKeys.activityList>>({
        queryKey: queryKeys.activityList(accountId, q),
        queryFn: () =>
          client.getActivity(accountId, {
            ...(q as Parameters<ApiClient['getActivity']>[1]),
            ...(cursor === undefined ? {} : { cursor }),
          }),
        ...base,
        placeholderData: keepPreviousData,
      }),

    // -- Creatives (API §8) — the gallery is period-keyed; one ad's creative and previews are not (T-088)
    creatives: (accountId: string, q: CreativesQuery) =>
      define<ApiResponse<CreativeTile[]>, ReturnType<typeof queryKeys.creativesList>>({
        queryKey: queryKeys.creativesList(accountId, q),
        queryFn: () => client.getCreatives(accountId, q).then(requirePeriod),
        ...base,
        placeholderData: keepPreviousData,
      }),
    creative: (accountId: string, adId: string) =>
      define<ApiResponse<CreativeDetail>, ReturnType<typeof queryKeys.creative>>({
        queryKey: queryKeys.creative(accountId, adId),
        queryFn: () => client.getCreative(accountId, adId),
        ...base,
      }),
    previews: (
      accountId: string,
      adId: string,
      placement: Placement,
      options?: { interactive?: boolean },
    ) =>
      define<ApiResponse<AdPreview>, ReturnType<typeof queryKeys.preview>>({
        queryKey: queryKeys.preview(accountId, adId, placement, options),
        queryFn: () =>
          options?.interactive
            ? client.getPreview(accountId, adId, placement, options)
            : client.getPreview(accountId, adId, placement),
        ...base,
      }),

    // -- Settings (API §10) — a settings change invalidates most of the account, so nothing here keeps previous
    settings: (accountId: string) =>
      define<ApiResponse<AccountSettings>, ReturnType<typeof queryKeys.settingsRecord>>({
        queryKey: queryKeys.settingsRecord(accountId),
        queryFn: () => client.getSettings(accountId),
        ...base,
      }),
    resultOptions: (accountId: string, campaignId: string) =>
      define<ApiResponse<ResultOption[]>, ReturnType<typeof queryKeys.resultOptions>>({
        queryKey: queryKeys.resultOptions(accountId, campaignId),
        queryFn: () => client.getResultOptions(accountId, campaignId),
        ...base,
      }),

    // -- Alerts (API §9.1–9.3) — the gallery, the rules, the builder's pickers and its live back-test (T-166)
    ...createAlertQueries(client, base),

    // -- Home's extras (API §3.3, §3.6) — the briefing and first-run (T-168); neither is period-keyed
    ...createHomeQueries(client, base),
  };
}

/**
 * The factories that carry `placeholderData: keepPreviousData` — a key change keeps the last answer's FIGURES on
 * screen until the next one lands (REQ-280 AC2). Two rules ride with that, and both are the reader's to keep:
 *
 *   1. **The kept answer is about ITS key, not the control's** (REQ-170 AC4 v2.7 — "a figure and its label
 *      change together, or the figure shows its pending state first"; D-876, BUG-200). A reader may show the
 *      kept figures, and beside them only words that are the ANSWER's — a period name off `meta.period`
 *      (`answeredPeriod`), a filter name off the rows — never the control's new choice, which is a label for
 *      an answer that has not arrived. Where the words can only be the control's, the figure goes pending.
 *   2. **Not every key change is "the same question over other dates".** The builder's back-test is keyed on
 *      the DRAFT and its labels come off the draft (the keystroke), so a kept answer there is the previous
 *      RULE's figure under the new rule's sentence — the defect REQ-170 AC4 v2.6 names. `alertBacktest`
 *      therefore carries no `keepPreviousData` (D-873: both readers refused it with `placeholderData: undefined`
 *      and go pending, D-861 / D-872), and is not in this list. `changePreview` omits it for the same reason
 *      (REQ-104 AC6).
 */
export const PERIOD_KEYED_QUERIES = [
  'summary',
  'timeseries',
  'entities',
  'entity',
  'tree',
  'spendPacing',
  'spendAllocation',
  'spendWasted',
  // Not period-keyed, but keeping the previous data is the same argument: REQ-106 AC2's log has its own range
  // control, and changing it must not blank the list under the reader (T-126).
  'activity',
  'creatives',
  // Keyed on the inbox's FILTERS (T-167): a class / rule / entity / state filter change keeps the last list on
  // screen while the next one loads — the log's argument again (REQ-173 AC5's filters).
  'alertInbox',
  'alertHistory',
] as const;
export type PeriodKeyedQuery = (typeof PERIOD_KEYED_QUERIES)[number];
